import React, { useEffect, useRef } from 'react';
import { useCall } from './CallProvider';

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'vtdFadeIn .3s ease',
  },
  card: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 20, padding: '40px 32px', textAlign: 'center',
    minWidth: 280, maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  avatar: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 16px', fontSize: 32, color: '#fff',
    animation: 'vtdPulseRing 1.5s infinite',
  },
  callerName: { color: '#fff', fontSize: 20, fontWeight: 600, margin: '0 0 4px' },
  callType: { color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: '0 0 32px' },
  buttons: { display: 'flex', gap: 24, justifyContent: 'center' },
  btn: {
    width: 60, height: 60, borderRadius: '50%', border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontSize: 24, transition: 'transform .15s',
  },
  acceptBtn: { background: 'linear-gradient(135deg, #00c853, #00e676)', color: '#fff' },
  declineBtn: { background: 'linear-gradient(135deg, #ff1744, #ff5252)', color: '#fff' },
};

const keyframes = `
@keyframes vtdFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes vtdPulseRing {
  0% { box-shadow: 0 0 0 0 rgba(102,126,234,0.5); }
  70% { box-shadow: 0 0 0 20px rgba(102,126,234,0); }
  100% { box-shadow: 0 0 0 0 rgba(102,126,234,0); }
}
`;

export default function IncomingCallModal() {
  const { callState, callInfo, answerCall, rejectCall, CALL_STATES } = useCall();
  const audioRef = useRef(null);

  useEffect(() => {
    if (callState === CALL_STATES.RINGING && callInfo?.direction === 'incoming') {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        gain.gain.value = 0.15;

        let playing = true;
        function ring() {
          if (!playing) return;
          osc.frequency.setValueAtTime(440, ctx.currentTime);
          osc.frequency.setValueAtTime(523, ctx.currentTime + 0.15);
          setTimeout(() => playing && ring(), 1500);
        }
        osc.start();
        ring();

        audioRef.current = { stop: () => { playing = false; osc.stop(); ctx.close(); } };
      } catch (_) {}
    }
    return () => { audioRef.current?.stop(); audioRef.current = null; };
  }, [callState, callInfo, CALL_STATES]);

  if (callState !== CALL_STATES.RINGING || callInfo?.direction !== 'incoming') return null;

  const initial = (callInfo.fromName || '?')[0].toUpperCase();

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.avatar}>{initial}</div>
          <p style={styles.callerName}>{callInfo.fromName}</p>
          <p style={styles.callType}>
            Incoming {callInfo.callType === 'video' ? 'Video' : 'Audio'} Call
          </p>
          <div style={styles.buttons}>
            <button
              style={{ ...styles.btn, ...styles.declineBtn }}
              onClick={rejectCall}
              title="Decline"
            >
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.52.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.19.29.44.29.71s-.11.51-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.74-1.69-1.36-2.67-1.85a1.003 1.003 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
            </button>
            <button
              style={{ ...styles.btn, ...styles.acceptBtn }}
              onClick={answerCall}
              title="Answer"
            >
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
